import { useEffect, useRef } from 'react';

/**
 * Re-seed a form's fields whenever the record behind it is (re)opened or a
 * fresher copy arrives.
 *
 * Every edit form in the app shares one shape: the dialog/drawer stays MOUNTED
 * while `open` toggles, so `defaultValues` alone fires once and never again,
 * and the entity is read by id on open so the authoritative record lands
 * asynchronously — after the form has already rendered from a list row.
 *
 * Getting the re-seed trigger wrong has produced the same bug twice (the
 * product drawer, then the category dialog), and it is silent:
 *
 * - **Seeding on a loading flag** never re-fires on a reopen. React Query
 *   serves the cached copy synchronously, so a reopened form never "loads"
 *   again — it seeds once from the STALE copy and discards the fresh answer.
 *   Reopening a record right after editing it then replays the pre-edit values,
 *   and saving writes them straight back over the edit.
 * - **A one-shot `seeded` ref** fails the same way for the same reason.
 * - **Seeding on the data object's identity** re-fires on every refetch that
 *   returns an equal-but-new object, clobbering what the merchant is typing.
 *
 * `dataUpdatedAt` is the correct trigger: React Query only advances it when a
 * fetch actually RESOLVES, so it fires on a genuine new record and stays put
 * across unrelated re-renders. Pair it with `staleTime: 0` on the by-id query
 * so every open re-reads rather than replaying a cached record.
 *
 * @param open    whether the form is currently visible
 * @param id      the record being edited (`undefined` while creating)
 * @param dataUpdatedAt  the by-id query's `dataUpdatedAt`
 * @param seed    applies the values — typically `() => reset(buildDefaults(…))`
 *
 * @example
 * const { data, dataUpdatedAt } = useThing(open ? id : undefined);
 * useFormSeed(open, id, dataUpdatedAt, () => reset(buildDefaults(data)));
 */
export function useFormSeed(
  open: boolean,
  id: string | undefined,
  dataUpdatedAt: number | undefined,
  seed: () => void,
): void {
  const key = `${String(open)}:${id ?? ''}:${String(dataUpdatedAt ?? 0)}`;
  const lastSeed = useRef<string | undefined>(undefined);
  // `seed` is called from an effect but deliberately not a dependency: callers
  // pass an inline closure over the freshly-read record, so a new identity every
  // render would re-seed continuously and wipe the merchant's input. The ref
  // keeps the latest closure without making it a trigger.
  //
  // Assigned in an effect rather than during render — a render-phase ref write
  // is unsafe under concurrent rendering (a render can be discarded, leaving
  // the ref pointing at a closure that never committed).
  const latestSeed = useRef(seed);
  useEffect(() => {
    latestSeed.current = seed;
  });

  useEffect(() => {
    // Closing resets the guard so the NEXT open always re-seeds, even when the
    // record itself has not changed since.
    if (!open) {
      lastSeed.current = undefined;
      return;
    }
    if (lastSeed.current === key) return;
    lastSeed.current = key;
    latestSeed.current();
  }, [open, key]);
}
